/**
 * TestConnectionModal — F18 T18.3.
 *
 * Otwiera się gdy user kliknie „Test połączenia" w `IntegrationsTab`. Pokazuje
 * stan job-a (pending / running / completed / failed) z React Query polling
 * (refetch co 1.5s aż status != pending/running, max 90s).
 *
 * Jeśli completed → pokazuje result (lista firm z Symfonii lub error message).
 * Jeśli failed → pokazuje error z Symfonii (np. „401 Unauthorized" / „connection
 *   refused" / „timeout").
 *
 * Timeout 90s w UI = bezpiecznik gdy agent nie odpowiada w ogóle (czekamy długo
 * żeby pokryć long-poll 60s + buffer na agenta wykonującego call do WebAPI).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

type JobStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface JobDto {
  id: string;
  integrationId: string;
  jobType: string;
  status: JobStatus;
  result: Record<string, unknown> | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

interface Props {
  open: boolean;
  /** Job ID z response POST /test-connection. null = jeszcze ładowanie initial POST */
  jobId: string | null;
  integrationName: string;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 90_000;

export function TestConnectionModal({ open, jobId, integrationName, onClose }: Props) {
  const { t } = useTranslation();
  const [timedOut, setTimedOut] = useState(false);
  // Odpytywanie o stan zadania zakończyło się błędem. Pamiętane TRWALE: sam `jobQ.isError` wraca do
  // `false` przy każdym kolejnym odpytaniu (zapytanie bez danych startuje od nowa), więc blokada
  // tła i Esc przełączałaby się w kółko. Świadomie nie wraca też po udanym kolejnym odpytaniu:
  // przejściowy błąd sieci nie ma zamykać użytkownika w oknie na czas trwania testu.
  const [pollFailed, setPollFailed] = useState(false);

  useEffect(() => {
    setPollFailed(false);
    if (!open) {
      setTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => setTimedOut(true), POLL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [open, jobId]);

  const jobQ = useQuery({
    queryKey: ['erp-sync-job', jobId],
    queryFn: async () => {
      const res = await api.get<{ job: JobDto }>(`/erp-sync-jobs/${jobId}`);
      return res.data.job;
    },
    enabled: !!jobId && open && !timedOut,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return POLL_INTERVAL_MS;
    },
  });

  useEffect(() => {
    if (jobQ.isError) setPollFailed(true);
  }, [jobQ.isError]);

  if (!open) return null;

  const job = jobQ.data ?? null;
  const status: JobStatus | 'requesting' | 'timeout' = timedOut
    ? 'timeout'
    : !jobId
      ? 'requesting'
      : (job?.status ?? 'pending');
  // Zadanie czeka lub trwa — pokazujemy spinner (niezależnie od błędu odpytywania, jak w wersji
  // bazowej okna: bez błędu odpytywania nie miga i nie znika).
  const waiting = status === 'requesting' || status === 'pending' || status === 'running';
  // Trwa zapytanie lub test — tło i Esc nie zamykają okna (X i „Zamknij" działają). Błąd
  // odpytywania zwalnia blokadę (inaczej okno wisiałoby zablokowane aż do limitu czasu).
  const running = waiting && !pollFailed;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('tenant.settings.integrations.testConnection.title', { name: integrationName })}
      size="md"
      testId="test-connection-modal"
      closeOnBackdrop={!running}
      closeOnEsc={!running}
      footer={
        <Button variant="primary" onClick={onClose} data-testid="test-connection-modal-close-btn">
          {t('common.close')}
        </Button>
      }
    >
      <div className="space-y-4" data-testid={`test-connection-modal-status-${status}`}>
        {waiting && (
          <div className="flex items-center gap-3 rounded-md bg-bg-secondary p-3">
            <Loader2 className="h-5 w-5 animate-spin text-accent-500" aria-hidden="true" />
            <div>
              <div className="font-medium text-text-primary">
                {status === 'requesting'
                  ? t('tenant.settings.integrations.testConnection.requesting')
                  : status === 'pending'
                    ? t('tenant.settings.integrations.testConnection.waitingForAgent')
                    : t('tenant.settings.integrations.testConnection.running')}
              </div>
              <div className="text-xs text-text-tertiary">
                {t('tenant.settings.integrations.testConnection.runningHint')}
              </div>
            </div>
          </div>
        )}

        {status === 'timeout' && (
          <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
            <Clock className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" aria-hidden="true" />
            <div>
              <div className="font-medium text-red-600 dark:text-red-400">
                {t('tenant.settings.integrations.testConnection.timeoutTitle')}
              </div>
              <div className="text-xs text-text-secondary">
                {t('tenant.settings.integrations.testConnection.timeoutHint')}
              </div>
            </div>
          </div>
        )}

        {status === 'completed' && job && (
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-md border border-green-500/30 bg-green-500/10 p-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" aria-hidden="true" />
              <div>
                <div className="font-medium text-green-700 dark:text-green-400">
                  {t('tenant.settings.integrations.testConnection.successTitle')}
                </div>
                <div className="text-xs text-text-secondary">
                  {t('tenant.settings.integrations.testConnection.successHint')}
                </div>
              </div>
            </div>

            {job.result && (
              <div>
                <div className="mb-1 text-xs font-medium text-text-secondary">
                  {t('tenant.settings.integrations.testConnection.resultLabel')}
                </div>
                <pre className="max-h-64 overflow-auto rounded-md border border-border-primary bg-bg-secondary p-3 font-mono text-xs text-text-primary">
                  {JSON.stringify(job.result, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {status === 'failed' && job && (
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
              <AlertCircle
                className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400"
                aria-hidden="true"
              />
              <div>
                <div className="font-medium text-red-600 dark:text-red-400">
                  {t('tenant.settings.integrations.testConnection.failedTitle')}
                </div>
                <div className="text-xs text-text-secondary">
                  {t('tenant.settings.integrations.testConnection.failedHint')}
                </div>
              </div>
            </div>

            {job.result && (
              <div>
                <div className="mb-1 text-xs font-medium text-text-secondary">
                  {t('tenant.settings.integrations.testConnection.errorLabel')}
                </div>
                <pre className="max-h-64 overflow-auto rounded-md border border-red-500/30 bg-red-500/5 p-3 font-mono text-xs text-red-700 dark:text-red-400">
                  {JSON.stringify(job.result, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
